import React, { useState } from 'react';
import {
  FaUser, FaMoneyBill, FaCog, FaPhotoVideo,
  FaIdBadge, FaUsers, FaHeart, FaGavel, FaTimes, FaCheckCircle, FaExclamationTriangle,
  FaComment, FaImage, FaPlay, FaVideo, FaMicrophone, FaFileAlt, FaGift, FaShieldAlt, FaLock,
  FaCoins, FaMagic, FaBullhorn, FaFileContract, FaWrench, FaBalanceScale, FaTrash, FaBell
} from 'react-icons/fa';
import { PiPackageDuotone } from 'react-icons/pi';
import { BsHourglassSplit } from 'react-icons/bs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { webPathFromDeepLink } from '../Utils/deepLink';

dayjs.extend(relativeTime);

const DISMISS_ANIMATION_MS = 200; // debe coincidir con la transición de .notif-item-exit

// Iconos por categoría
const categoryIcons = {
  user: <FaUser className="text-white" />,
  pack: <PiPackageDuotone className="text-white" />,
  payment: <FaMoneyBill className="text-white" />,
  system: <FaCog className="text-white" />,
  content: <FaPhotoVideo className="text-white" />,
  profile: <FaIdBadge className="text-white" />,
  feed: <FaUsers className="text-white" />,
  social: <FaHeart className="text-white" />,
  moderation: <FaGavel className="text-white" />
};

// Vocabulario de data.icon que manda el backend.
const namedIcons = {
  chat: FaComment,
  image: FaImage,
  play: FaPlay,
  camera: FaVideo,
  audio: FaMicrophone,
  file: FaFileAlt,
  gift: FaGift,
  pack: PiPackageDuotone,
  profile: FaIdBadge,
  shield: FaShieldAlt,
  lock: FaLock,
  coin: FaCoins,
  heart: FaHeart,
  sparkle: FaMagic,
  megaphone: FaBullhorn,
  document: FaFileContract,
  wrench: FaWrench,
  check: FaCheckCircle,
  scale: FaBalanceScale,
  trash: FaTrash
};

const messageTypeIcons = { image: 'image', video: 'play', audio: 'audio', file: 'file' };

// Respaldo cuando data.icon falta (filas antiguas, moderación, videollamadas).
const fallbackIconName = (notification) => {
  const type = String(notification.type || '');
  if (type === 'chat_message') return messageTypeIcons[notification.data?.message_type] || 'chat';
  if (type === 'chat_gift') return 'gift';
  if (/^(videochat|live)_(request|accept|reject)$/.test(type)) return 'camera';
  if (type === 'moderation_admin_response' || type === 'moderation_appeal_submitted') return 'image';
  if (type.startsWith('pack_')) return 'pack';
  if (type === 'photo_approved' || type === 'photo_refused') return 'profile';
  if (type.startsWith('payment_')) return 'coin';
  if (type === 'system_update') return 'megaphone';
  if (type === 'social_model_favorited') return 'heart';
  if (type === 'social_chat_suggestion') return 'sparkle';
  if (type === 'claim_opened' || type === 'claim_resolved') return 'scale';
  if (type === 'deletion_resolved') return 'trash';
  return null;
};

// 1) data.icon si lo conocemos; 2) respaldo por type; 3) icono por categoría; 4) campana.
const getNotificationIcon = (notification) => {
  const name = namedIcons[notification.data?.icon] ? notification.data.icon : fallbackIconName(notification);
  const Named = name && namedIcons[name];
  if (Named) return <Named className="text-white" />;
  const byCategory = categoryIcons[notification.category];
  return byCategory || <FaBell className="text-white" />;
};

// Decodifica entidades HTML (&amp;, &eacute;, …) sin interpretar etiquetas:
// el contenido de un <textarea> es texto plano, nunca se ejecuta ni renderiza como HTML
const decodeHtmlEntities = (html) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

const NotificationItem = ({ notification, onClick, darkMode, onDismiss, navigate, userRole, unread = true, dismissible = true }) => {
  if (!notification || typeof notification !== 'object') return null;

  const [isClosing, setIsClosing] = useState(false);

  const icon = getNotificationIcon(notification);
  const isChat = notification.category === 'chat';
  const isPack = notification.category === 'pack';
  const packTitle = notification.data?.pack_title;
  const isApproved = notification.type === 'pack_approved';
  const isRefused = notification.type === 'pack_refused';
  const isSuggestion = notification.type === 'pack_suggestion';
  // Varios avisos del mismo tipo y actor agrupados en una fila (count). Si el título ya trae
  // el número ("3 solicitudes de videollamada") no se repite en la etiqueta.
  const count = Number(notification.count) || 1;
  const titleHasCount = String(notification.title || '').startsWith(`${count} `);

  const handleDismiss = () => {
    if (!dismissible) {
      onDismiss?.(notification);
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onDismiss(notification);
    }, DISMISS_ANIMATION_MS);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  // El backend manda el destino como path canónico en data.deep_link (sin rol);
  // se traduce a la ruta local. Si no viene, se cae a la lógica por
  // tipo/chat_id de siempre.
  const handleClick = () => {
    const deepLinkPath = webPathFromDeepLink(notification.data?.deep_link, userRole);

    if (deepLinkPath && navigate) {
      handleDismiss();
      setTimeout(() => {
        navigate(deepLinkPath);
      }, DISMISS_ANIMATION_MS);
    } else if (isChat && navigate) {
      handleDismiss();
      setTimeout(() => {
        navigate(`/${userRole}/chat/${notification.data?.chat_id}`);
      }, DISMISS_ANIMATION_MS);
    } else if (isSuggestion && navigate && notification.data?.pack_id) {
      handleDismiss();
      setTimeout(() => {
        navigate(`/${userRole}/packs/edit/${notification.data.pack_id}`);
      }, DISMISS_ANIMATION_MS);
    } else {
      onClick(notification);
    }
  };

  return (
    <div
      className={`relative p-4 border-b rounded-[10px] flex gap-3 notif-item cursor-pointer ${
        isClosing ? 'notif-item-exit' : ''
      } ${darkMode ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
    >
      {/* Icono de categoría */}
      <div className="relative min-w-[48px]">
        <div className="bg-rose-500 p-2 rounded-full flex items-center justify-center w-10 h-10">
          {icon}
        </div>
        {isPack && (isApproved || isRefused) && (
          <div className="absolute top-4 -left-2 mt-6">
            {isApproved && <FaCheckCircle className="text-rose-500 text-base" />}
            {isRefused && <FaExclamationTriangle className="text-rose-500 text-base" />}
          </div>
        )}
      </div>

      {/* Contenido */}
      <div
        className="flex-grow flex flex-col gap-1 mt-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 rounded"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <h4 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-sm font-semibold`}>
          {notification.title}
          {count > 1 && !titleHasCount && (
            <span
              className="ml-2 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-none align-middle"
              title={`${count} avisos`}
            >
              {count}
            </span>
          )}
        </h4>

        {/* Título del pack como TAG visual */}
        {isPack && packTitle && (
          <div className="inline-block text-xs px-2 py-0.5 rounded-full bg-rose-500 text-rose-600 font-medium dark:bg-rose-800 dark:text-rose-200 w-fit mb-1">
            {decodeHtmlEntities(packTitle)}
          </div>
        )}

        {/* Cuerpo */}
        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} text-xs line-clamp-2`}>
          {notification.body || ''}
        </p>

        {/* Fecha relativa ("hace 5 minutos") */}
        <p className="text-[10px] text-gray-400 mt-1">
          {notification.created_at ? dayjs(notification.created_at).locale('es').fromNow() : ''}
        </p>
      </div>

      {/* Punto indicador de "nuevo" */}
      {unread && (
        <span className="absolute top-2 right-10 w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
      )}

      {/* Botón de cerrar */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 bg-white text-gray-700 hover:text-gray-900 rounded-full p-1 shadow"
          title="Dismiss"
          aria-label="Dismiss notification"
        >
          <FaTimes size={14} />
        </button>
      )}
    </div>
  );
};

export default NotificationItem;
